export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const THEME_STORAGE_KEY = "theme-preference";

/**
 * Gets the user's system theme preference
 */
export const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/**
 * Gets the stored theme preference from localStorage
 */
export const getStoredTheme = (): ThemeMode | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && ["light", "dark", "system"].includes(stored)) {
      return stored as ThemeMode;
    }
  } catch (error) {
    console.warn("Failed to read theme from localStorage:", error);
  }
  return null;
};

/**
 * Stores the theme preference in localStorage
 */
export const storeTheme = (theme: ThemeMode): void => {
  if (typeof window === "undefined") return;
  try {
    if (theme === "system") {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  } catch (error) {
    console.warn("Failed to store theme in localStorage:", error);
  }
};

/**
 * Gets the current theme mode (what the user has selected)
 */
export const getCurrentThemeMode = (): ThemeMode => {
  const stored = getStoredTheme();
  return stored || "system";
};

/**
 * Resolves the actual theme to apply (light or dark)
 */
export const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  if (mode === "system") {
    return getSystemTheme();
  }
  return mode;
};

/**
 * Gets the currently applied theme from the data-theme attribute
 */
export const getCurrentAppliedTheme = (): ResolvedTheme => {
  if (typeof document === "undefined") return "light";
  const dataTheme = document.documentElement.getAttribute("data-theme");
  return dataTheme === "dark" ? "dark" : "light";
};

/**
 * Updates the theme-color meta tag for mobile browsers
 */
const updateThemeColorMeta = (theme: ResolvedTheme): void => {
  if (typeof document === "undefined") return;

  const themeColors = {
    light: "#ffffff",
    dark: "#27272a",
  };

  let metaThemeColor = document.querySelector(
    'meta[name="theme-color"]:not([media])'
  ) as HTMLMetaElement;

  if (!metaThemeColor) {
    metaThemeColor = document.createElement("meta");
    metaThemeColor.name = "theme-color";
    document.head.appendChild(metaThemeColor);
  }

  metaThemeColor.content = themeColors[theme];
};

/**
 * Applies the theme to the document
 */
export const applyTheme = (theme: ResolvedTheme): void => {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);

  // Also update the class for compatibility with some CSS frameworks
  document.documentElement.classList.remove("light", "dark");
  document.documentElement.classList.add(theme);

  // Update theme-color meta tag for mobile browsers
  updateThemeColorMeta(theme);
};

/**
 * Sets the theme mode and applies it
 */
export const setTheme = (mode: ThemeMode): void => {
  storeTheme(mode);
  const resolvedTheme = resolveTheme(mode);
  applyTheme(resolvedTheme);
};

/**
 * Initializes the theme on app load
 */
export const initializeTheme = (): ThemeMode => {
  const mode = getCurrentThemeMode();
  const resolvedTheme = resolveTheme(mode);
  applyTheme(resolvedTheme);
  return mode;
};

/**
 * Creates a system theme change listener
 */
export const createSystemThemeListener = (
  callback: (theme: ResolvedTheme) => void
) => {
  if (typeof window === "undefined") return () => {};

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  const handleChange = (e: MediaQueryListEvent) => {
    const currentMode = getCurrentThemeMode();
    if (currentMode === "system") {
      const newTheme = e.matches ? "dark" : "light";
      applyTheme(newTheme);
      callback(newTheme);
    }
  };

  mediaQuery.addEventListener("change", handleChange);

  // Return cleanup function
  return () => {
    mediaQuery.removeEventListener("change", handleChange);
  };
};

// React Hook
import { useState, useEffect } from "react";

export const useTheme = () => {
  const [themeMode, setThemeMode] = useState<ThemeMode>("system");
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>("light");

  useEffect(() => {
    // Initialize theme on mount
    const initialMode = initializeTheme();
    setThemeMode(initialMode);
    setResolvedTheme(resolveTheme(initialMode));

    // Listen for system theme changes
    const cleanupSystemThemeListener = createSystemThemeListener((newTheme) => {
      setResolvedTheme(newTheme);
    });

    return cleanupSystemThemeListener;
  }, []);

  const updateTheme = (mode: ThemeMode) => {
    setTheme(mode);
    setThemeMode(mode);
    setResolvedTheme(resolveTheme(mode));
  };

  return {
    themeMode,
    resolvedTheme,
    setTheme: updateTheme,
    isSystem: themeMode === "system",
  };
};
